import React, { useRef, useCallback, useState, useEffect } from 'react';

const VideoTest: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Track fullscreen state
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Enter fullscreen + lock to landscape on mobile
  const enterFullscreen = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;

    try {
      await el.requestFullscreen();
      // Attempt to lock orientation to landscape (mobile browsers that support it)
      try {
        await (window.screen.orientation as any).lock('landscape');
      } catch {
        // Orientation lock not supported or not allowed — that's fine
      }
    } catch {
      // Fullscreen not supported
    }
  }, []);

  // Exit fullscreen
  const exitFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
      try {
        (window.screen.orientation as any).unlock();
      } catch {
        // ignore
      }
    } catch {
      // ignore
    }
  }, []);

  return (
    <div className="w-full max-w-5xl mx-auto px-2 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-brand-text-primary">
          Video Test
        </h1>
        <p className="text-sm sm:text-base text-brand-text-secondary mt-1">
          Responsive Vimeo embed with fullscreen &amp; landscape support
        </p>
      </div>

      {/* Video container */}
      <div
        ref={containerRef}
        className={`relative w-full bg-black rounded-lg overflow-hidden shadow-lg ${
          isFullscreen ? 'flex items-center justify-center' : ''
        }`}
      >
        {/* 16:9 aspect ratio wrapper */}
        <div
          className={
            isFullscreen
              ? 'w-full h-full'
              : 'relative w-full'
          }
          style={isFullscreen ? undefined : { paddingTop: '56.25%' }}
        >
          <iframe
            src="https://player.vimeo.com/video/1166346432?h=2d75636ba9&badge=0&autopause=0&player_id=0&app_id=58479"
            className="absolute top-0 left-0 w-full h-full"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            title="Video Test"
          />
        </div>

        {/* Fullscreen toggle button — overlaid bottom-right */}
        <button
          onClick={isFullscreen ? exitFullscreen : enterFullscreen}
          className="absolute bottom-3 right-3 z-10 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors duration-200 touch-target"
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? (
            // Collapse icon
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 9L4 4m0 0v4m0-4h4m6 10l5 5m0 0v-4m0 4h-4M9 15l-5 5m0 0h4m-4 0v-4m10-6l5-5m0 0h-4m4 0v4" />
            </svg>
          ) : (
            // Expand icon
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0 0l-5-5M4 16v4m0 0h4m-4 0l5-5m11 5h-4m4 0v-4m0 0l-5 5" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile hint */}
      <p className="mt-3 text-xs sm:text-sm text-brand-text-tertiary text-center md:hidden">
        Tap the fullscreen button or rotate your device to landscape for the best viewing experience.
      </p>
    </div>
  );
};

export default VideoTest;
